import { Autocomplete, AutocompleteChangeDetails, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { contactSelectorSelector, setContactSearchResults } from './reducers/ContactSelectorReducer';
import ContactNameModel from './models/ContactNameModel';
import ContactApi, { SearchContactNameParam } from '../Contact/api/ContactApi';

interface ContactSelectorProps {
    selectedContactId?: string,
    currentContactName?: string,
    label: string,
    useFocusMode?: boolean,
    focusValue?: number,
    useFreeText?: boolean,
    className?: string,
    triggerInitContactName?: number,
    onContactIdChange?: (value: string) => void,
    onContactNameChange?: (value: string) => void,
    onInputChange?: (value: string) => void,
}

export const searchContactName = (keyword: string, callBack: (contacts: ContactNameModel[]) => void) => {
    ContactApi.SearchContactName({
        keyword: keyword
    } as SearchContactNameParam)
        .then((response: BaseResponse<ContactNameModel[]>) => {
            if (response.success) {
                callBack(response.data);
            } else {
                Utility.Alert.Error(response.message);
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
        })
};

const ContactSelector: React.FunctionComponent<ContactSelectorProps> = (props) => {
    const dispatch = useDispatch();
    const contactSelectorData = useSelector(contactSelectorSelector);
    const [searchResults, setSearchResults] = useState<ContactNameModel[]>([]);
    const [selectedContact, setSelectedContact] = useState<ContactNameModel | null>(null);
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [contactName, setContactName] = useState<string>("");
    const textboxRef = React.createRef<HTMLInputElement>();

    useEffect(() => {
        let selectedContactById = _.find(contactSelectorData.searchResults, (item) => item.id === props.selectedContactId);
        setSelectedContact(selectedContactById ? selectedContactById : null);

        if (props.useFreeText == true) {
            if (props.currentContactName) {
                setContactName(props.currentContactName);
            } else {
                setContactName(selectedContactById ? selectedContactById.companyName : "");
            }
        }

        if (searchResults && searchResults.length > 0) {
            setSearchResults(contactSelectorData.searchResults);
        }
    }, [props.selectedContactId])

    useEffect(() => {
        if (props.useFreeText == true) {
            setContactName(props.currentContactName ? props.currentContactName : "");
        }
    }, [props.currentContactName])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchKeyword && searchKeyword.length > 0) {
                searchContactName(searchKeyword, (contacts: ContactNameModel[]) => {
                    if (contacts && contacts.length > 0) {
                        setSearchResults(contacts);

                        let searchResults = Utility.CloneObject(contactSelectorData.searchResults) as ContactNameModel[];
                        _.each(contacts, (contact) => {
                            searchResults.push(contact);
                        });

                        searchResults = _.uniqBy(searchResults, (item: ContactNameModel) => {
                            return item.id;
                        });

                        dispatch(setContactSearchResults(searchResults));
                    } else {
                        setSearchResults([]);
                    }
                })
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchKeyword])

    useEffect(() => {
        if (props.useFocusMode === true) {
            textboxRef.current?.focus();
        }
    }, [props.focusValue])

    return (
        <>
            <Autocomplete
                fullWidth
                className={props.className ? props.className : ""}
                freeSolo={props.useFreeText === true ? true : false}
                inputValue={props.useFreeText === true ? contactName : props.currentContactName}
                value={props.useFreeText == true ? undefined : selectedContact}
                options={searchResults}
                renderInput={(params) => <TextField {...params} label={props.label} inputRef={textboxRef} />}
                getOptionLabel={(option: ContactNameModel) => {
                    return option.companyName;
                }}
                onInputChange={(event: React.SyntheticEvent, value: string, reason: string) => {
                    setSearchKeyword(value);
                    
                    if (props.onInputChange) {
                        props.onInputChange(value ? value : "");
                    }

                    if (props.onContactNameChange) {
                        props.onContactNameChange(value.toUpperCase());
                    }

                    if (reason === "reset" && value.length === 0) {
                        if (props.onContactIdChange) {
                            props.onContactIdChange("");
                        }
                    }
                }}
                onChange={(event: React.SyntheticEvent, value: string | ContactNameModel | null, reason: string, details?: AutocompleteChangeDetails<ContactNameModel> | undefined) => {
                    if (value == null) {
                        if (props.onContactIdChange) {
                            props.onContactIdChange("");
                        }
                    } else {
                        let option = value as ContactNameModel;
                        if (option) {
                            setSelectedContact(option);
                            if (props.onContactIdChange) {
                                props.onContactIdChange(option.id);
                            }
                        }
                    }
                }}
            />
        </>
    )
};

export default ContactSelector;